import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useOverrideBack } from '../backOverride'
import { useSelection } from '@shared/state/SelectionContext'
import { WwLoaderFill } from '@shared/components/WwLoader'
import { moodboardWaiting, sendMoodboard, stageMoodboardPicture } from '@shared/services/miroLayouts'
import { markStockUsed } from '@shared/services/stockService'
import { personalProject } from '@shared/services/pictureService'
import { uploadProjectFile } from '@shared/services/projectFileService'
import { putClip } from '@shared/services/projectClip'
import { copyProjectFiles } from '@shared/services/projectFileService'
import { createConversation, renameConversation } from '@shared/services/chatService'
import { saveCanvas } from '@shared/services/canvasService'
import { HoldButton } from '../components/HoldButton'
import { openProjectLater } from '../openProject'
import { bitmapOf, cutForBoard } from './moodboardRender'
import { renderFloat } from '@shared/moodboard/float'
import { MoodboardStart, fromAsset, fromStock, type SourcePicture } from './MoodboardStart'
import { getAssetRepository } from '@shared/services/assetRepository'
import { planFor, smartFor } from '@shared/services/smartSearchService'
import { searchStock } from '@shared/services/stockService'
import {
  MoodboardBoard,
  freshSettings,
  readPictures,
  shownWhole,
  type BoardApi,
  type BoardPicture,
  type BoardSettings,
} from '../moodboard/MoodboardBoard'
import { cutMoodboardFrame } from '../moodboard/cutFrame'
import './moodboard.css'

/**
 * Mood board (Huanrui, 2026-10-12)
 * -------------------------------------------------------------
 * Two ways in. From the skills, it opens on a search (MoodboardStart.tsx):
 * Visual Language, Unsplash, Pexels, picked into one tray. From the Mood board
 * button beside Visual Language's search box, it starts straight from what
 * is selected there.
 *
 * Then the pictures are read for their shapes and colours (the grey loader
 * shows meanwhile), put in an order that flows by colour with the first one
 * leading, and laid on a 16:9 sheet in one of four layouts
 * (shared/moodboard/layout.ts). Beside the sheet: the layout, the ground, the
 * foot strip with its title, words and colours.
 *
 * The pictures are moved by dragging one onto another (they swap frames).
 * Double-click one to re-cut it: drag to move it in its frame, drag the
 * picture's corners (or the wheel) to make it bigger or smaller, with the
 * rest of the sheet darkened (2026-10-15). The round
 * button in a picture's corner swaps it for the next spare, or, with none
 * left, a new one found the way the board's were (the same words, the same
 * source) and of a shape that suits the frame; Delete takes a
 * clicked picture off. There is no "lead" any more (Huanrui, 2026-10-15):
 * whichever picture is dragged into the big frame is the big one.
 *
 * Click a text to change its words and type -- Miro's own typefaces only
 * (shared/data/miroFonts.ts), so what is set here is what the board shows.
 * Unsplash and Pexels photographers are credited in a small line under it.
 *
 * The sheet itself, and everything done on it, is MoodboardBoard
 * (web/moodboard/MoodboardBoard.tsx, 2026-10-16), shared with the canvas's
 * Mood board card. This page chooses the pictures and acts on the sheet.
 *
 * ONE-SHOT, like Edit Text: nothing is saved. Send to Miro parks the sheet for
 * the WonderWorks panel, which draws it as a 16:9 frame and crops the pictures
 * itself; leaving the page loses the board.
 */

const POLL_MS = 5000

interface Arrival {
  /** Came from the Mood board button: start from the selection. */
  fromSelection?: boolean
  title?: string
  keywords?: string[]
}

/**
 * Visual Language pictures for the words the board was made from, to refill
 * a frame: a sentence understood and scored as in every search box
 * (2026-11-03), a word or two -- or a sentence that found nothing -- as typed.
 */
async function libraryPool(words: string): Promise<SourcePicture[]> {
  const repository = getAssetRepository()
  const plan = words ? await planFor(words, ['visual-language']) : null
  const smart = plan ? smartFor(plan, 'visual-language') : null
  if (smart) {
    const found = await repository.search({ library: 'visual-language', limit: 60, smart }).catch(() => null)
    if (found && found.items.length > 0) return found.items.map(fromAsset)
  }
  return (await repository.search({ text: words || undefined, library: 'visual-language', limit: 60 })).items.map(fromAsset)
}

export function MoodboardPage() {
  const { selectedAssets } = useSelection()
  const arrival = (useLocation().state ?? {}) as Arrival

  /** 'start': choosing pictures; 'making': reading them; 'board': the sheet. */
  const [phase, setPhase] = useState<'start' | 'making' | 'board'>(
    arrival.fromSelection && selectedAssets.length > 0 ? 'making' : 'start',
  )
  const [makingCount, setMakingCount] = useState(0)
  /** The sheet to show: its pictures and how it starts. A new one each time the pictures are chosen. */
  const [made, setMade] = useState<{ run: number; pictures: BoardPicture[]; initial: BoardSettings } | null>(null)

  // ---------------------------------------------------------- making
  const making = useRef(0)
  /** The words the pictures were searched with, for finding more like them. */
  const searched = useRef('')
  const found = useRef(new Map<string, SourcePicture[]>())
  const make = useCallback((sources: SourcePicture[], words: { title?: string; keywords?: string[] }) => {
    if (sources.length === 0) return
    const run = ++making.current
    searched.current = (words.title ?? '').trim() || (words.keywords ?? []).join(' ')
    found.current.clear()
    setPhase('making')
    setMakingCount(sources.length)
    const began = Date.now()
    void readPictures(sources).then(({ pictures, order }) => {
      if (run !== making.current) return
      // Long enough that the loader reads as work, not a flicker.
      window.setTimeout(() => {
        if (run !== making.current) return
        setMade({
          run,
          pictures,
          initial: freshSettings(order, {
            title: words.title,
            keywords: (words.keywords ?? []).slice(0, 3).join(' · ').toUpperCase(),
          }),
        })
        setPhase('board')
      }, Math.max(0, 700 - (Date.now() - began)))
    })
  }, [])

  // From the Mood board button: the selection as it was when the page opened.
  const started = useRef(false)
  useEffect(() => {
    if (started.current || !arrival.fromSelection || selectedAssets.length === 0) return
    started.current = true
    make(selectedAssets.map(fromAsset), { title: arrival.title, keywords: arrival.keywords })
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * No spares left (Huanrui, 2026-10-15): a new picture, found the way the
   * board's own were -- the same words, from the same source as the one it
   * replaces (Visual Language or Unsplash) -- never one already used, and of
   * a shape close to the frame's, so it is cut little. Picked at random
   * from the five best fits, so pressing again gives another. What a source
   * answered is kept, so each press does not search again.
   */
  const findMore = useCallback(
    async ({ shape, was, used }: { shape: number; was: BoardPicture; used: Set<string> }): Promise<SourcePicture> => {
      const words = searched.current
      const fromUnsplash = was.src.source === 'unsplash' && words !== ''
      const key = `${fromUnsplash ? 'unsplash' : 'library'}|${words}`
      let pool = found.current.get(key)
      if (!pool) {
        pool = fromUnsplash
          ? (await searchStock('unsplash', words, 1 + Math.floor(Math.random() * 3))).pictures.map(fromStock)
          : await libraryPool(words)
        found.current.set(key, pool)
      }
      const fits = pool
        .filter((one) => !used.has(one.key))
        .map((one) => ({ one, off: Math.abs(Math.log((one.width > 0 && one.height > 0 ? one.width / one.height : 1) / shape)) }))
        .sort((a, b) => a.off - b.off)
        .slice(0, 5)
      if (fits.length === 0) throw new Error('No more pictures like these. Choose other pictures for more.')
      return fits[Math.floor(Math.random() * fits.length)].one
    },
    [],
  )

  /** Was the choosing page ever shown? If not (came from Visual Language), Back goes back there. */
  const navigate = useNavigate()
  const choseHere = useRef(phase === 'start')
  if (phase === 'start') choseHere.current = true
  const back = useCallback(() => {
    if (choseHere.current) {
      making.current += 1
      setPhase('start')
    } else navigate(-1)
  }, [navigate])
  useOverrideBack(phase === 'start' ? null : back)

  // ---------------------------------------------------------- drawing
  /*
   * The choosing page stays in place, hidden, while the board is made and
   * shown, so Back (or Choose other pictures) finds the wall, the words and
   * the bucket as they were.
   */
  const start = (
    <div style={{ display: phase === 'start' ? 'contents' : 'none' }}>
      <MoodboardStart selected={selectedAssets} onMake={(sources, words) => make(sources, { title: words })} />
    </div>
  )
  if (phase === 'start') return <>{start}</>

  if (phase === 'making' || !made) {
    return (
      <>
        {start}
        <section className="mbd mbd--making">
          <div className="mbd__making">
            <WwLoaderFill size="large" label="Making the mood board" />
          </div>
          <p className="mbd__making-note">Laying out {makingCount || selectedAssets.length} pictures…</p>
        </section>
      </>
    )
  }

  return (
    <>
      {start}
      <MoodboardBoard
        key={made.run}
        pictures={made.pictures}
        initial={made.initial}
        findMore={findMore}
        hint={
          <button type="button" className="mbd__link" onClick={() => setPhase('start')}>
            Choose other pictures
          </button>
        }
        footer={(api) => <PageActions api={api} />}
      />
    </>
  )
}

/** Send to Miro and Copy to project, at the foot of the side column. */
function PageActions({ api }: { api: BoardApi }) {
  const [sending, setSending] = useState(false)
  const [copying, setCopying] = useState(false)
  /** Pictures cut so far while sending. */
  const [prepared, setPrepared] = useState<{ done: number; total: number } | null>(null)
  const [sentId, setSentId] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const { sheet, pictures } = api
  const count = sheet.slots.length

  const send = async () => {
    const board = api.board()
    setSending(true)
    setError('')
    try {
      /*
       * Faster (2026-10-12): every picture is cut here, now, and left with
       * the Worker, so the panel only has Miro fetch them. One that cannot be
       * prepared is left for the panel to cut, as before.
       */
      /*
       * ORIGINALS (Huanrui, 2026-10-16): a library picture shown whole goes
       * as its own file, signed by the Worker and fetched by Miro at full size
       * (`original`, as a frame from a project's board does). One the sheet
       * cuts is cut from the original at full size, nothing scaled down.
       */
      board.pictures.forEach((picture, n) => {
        // Not one with a translucent ground: that is cut, and so made solid (2026-10-23).
        if (picture.ref && shownWhole(sheet.slots[n].crop) && !sheet.slots[n].turn && pictures[sheet.slots[n].pic].clear !== 'semi') {
          picture.original = true
        }
      })
      const queue = board.pictures
        .map((picture, n) => ({ picture, slot: sheet.slots[n] }))
        .filter((one) => !one.picture.original)
      const total = queue.length
      let done = 0
      setPrepared({ done, total })
      const worker = async () => {
        for (let next = queue.shift(); next; next = queue.shift()) {
          const { picture, slot } = next
          const src = pictures[slot.pic].src
          try {
            const bytes = await cutForBoard({ url: src.full || src.url, x: slot.x, y: slot.y, w: slot.w, h: slot.h, crop: slot.crop, turn: slot.turn }, true)
            picture.staged = await stageMoodboardPicture(bytes)
          } catch (caught) {
            console.warn('[WonderWorks] picture left for the panel to cut', caught)
          }
          done += 1
          setPrepared({ done, total })
        }
      }
      await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker))
      // Lifted pictures (2026-10-23): drawn here with their outline and turn
      // and parked for the panel, which places them last, on top. One that
      // could not be is drawn by the panel instead (its `float` still says how).
      await Promise.all(
        (board.floats ?? []).map(async (picture, n) => {
          const one = sheet.floats[n]
          if (!one) return
          const src = pictures[one.pic].src
          try {
            const image = await bitmapOf(src.full || src.url)
            try {
              picture.staged = await stageMoodboardPicture(await renderFloat(image, one.spec))
            } finally {
              image.close()
            }
          } catch (caught) {
            console.warn('[WonderWorks] lifted picture left for the panel to draw', caught)
          }
        }),
      )
      const id = await sendMoodboard(board)
      // Unsplash asks to be told when a picture is used.
      void markStockUsed(
        [...sheet.slots.map((slot) => slot.pic), ...sheet.floats.map((one) => one.pic)]
          .map((pic) => pictures[pic].src.downloadLocation)
          .filter((one): one is string => Boolean(one)),
      )
      setSentId(id)
      setNote('Waiting for the WonderWorks panel. Open it on a Miro board and press Add to board.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'It could not be sent')
    } finally {
      setSending(false)
      setPrepared(null)
    }
  }

  /**
   * Copy to project (Huanrui, 2026-10-12): the mood board as a FRAME on the
   * project's board, the same frame it makes on Miro (cutFrame.ts). The cut
   * pictures are kept in this person's hidden project, then copied into
   * whichever project takes them, the way the libraries' Copy to project is
   * offered (projectClip.ts).
   */
  const prepareFrame = async () => {
    const project = await personalProject()
    const made = await cutMoodboardFrame(api.board(), sheet, pictures, (blob, name, size) =>
      uploadProjectFile(project, new File([blob], name, { type: blob.type || 'image/jpeg' }), size),
    )
    // Unsplash asks to be told when a picture is used.
    void markStockUsed(
      [...sheet.slots.map((slot) => slot.pic), ...sheet.floats.map((one) => one.pic)]
          .map((pic) => pictures[pic].src.downloadLocation)
          .filter((one): one is string => Boolean(one)),
    )
    return made
  }

  /** A click: offered to every project (projectClip.ts). */
  const copyToProject = async () => {
    if (copying) return
    setCopying(true)
    setError('')
    setNote('')
    try {
      const made = await prepareFrame()
      putClip('Mood board', made.items, { nodes: made.piece, edges: [], files: made.files })
      setNote('Copied. Open any project and it will be offered there, as a frame. Hold the button for a new project.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'It could not be copied')
    } finally {
      setCopying(false)
    }
  }

  /**
   * Held (Huanrui, 2026-10-12, as on the generate page): a new project named
   * after the mood board, with the frame on its board, opened in a new tab.
   * The tab is opened at the end of the hold, while it still counts as the
   * person's own press.
   */
  const copyToNewProject = async () => {
    if (copying) return
    setCopying(true)
    setError('')
    setNote('')
    const tab = openProjectLater()
    try {
      const [made, ready] = await Promise.all([createConversation(), prepareFrame()])
      const copied = await copyProjectFiles(made.id, ready.files)
      const nodes = ready.piece.map((node) => {
        const fileId = (node.data as { fileId?: unknown }).fileId
        return typeof fileId === 'string' && copied[fileId] ? { ...node, data: { ...node.data, fileId: copied[fileId] } } : node
      })
      await saveCanvas(made.id, { pages: [{ id: 'page-1', name: 'Page 1' }], nodes, edges: [] })
      await renameConversation(made.id, ready.title).catch(() => undefined)
      tab.go(`/projects/${made.id}`)
      setNote('A new project with this mood board opened in a new tab.')
    } catch (caught) {
      tab.cancel()
      setError(caught instanceof Error ? caught.message : 'A project could not be made')
    } finally {
      setCopying(false)
    }
  }

  useEffect(() => {
    if (!sentId) return
    const timer = window.setInterval(() => {
      void moodboardWaiting(sentId).then((waiting) => {
        if (waiting === false) {
          setNote('On the board.')
          setSentId('')
        }
      })
    }, POLL_MS)
    return () => window.clearInterval(timer)
  }, [sentId])

  return (
    <>
      {error ? <p className="mbd__error">{error}</p> : null}
      {note ? <p className="mbd__note">{note}</p> : null}
      <button type="button" className="mbd__send" onClick={() => void send()} disabled={sending || count === 0}>
        {sending ? (prepared && prepared.done < prepared.total ? `Preparing ${prepared.done} of ${prepared.total}…` : 'Sending…') : 'Send to Miro'}
      </button>
      <HoldButton
        className="mbd__copy"
        onClick={() => void copyToProject()}
        onHold={() => void copyToNewProject()}
        disabled={copying || count === 0}
        title="Click: offer it to a project. Hold: a new project with it."
      >
        {copying ? 'Copying…' : 'Copy to project'}
      </HoldButton>
      <p className="mbd__quiet">Nothing here is saved. Leaving the page loses this board; Copy to project keeps it as a frame on a project's board.</p>
    </>
  )
}
